import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';

import { LoggerService } from '@app/common/services/logger.service';
import { LoggerType } from '@app/types/logger.types';
import { ContactFormFile } from '@app/types/contact-form.types';

@Component({
  selector: 'app-files-input',
  imports: [],
  templateUrl: './files-input.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './files-input.component.scss'
})
export class FilesInputComponent {
  private loggerService = inject(LoggerService);

  @Input() inputId: string;
  @Output() filesAdded = new EventEmitter<ContactFormFile[]>();
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  addedFiles: ContactFormFile[] = [];

  readonly ALLOWED_FILE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.pdf'];
  readonly MAX_FILES_COUNT = 5;

  private readonly ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpg', 'image/jpeg', 'image/png'];
  private readonly MAX_FILE_SIZE = 2 * 1024 * 1024;

  selectFiles(): void {
    this.fileInput.nativeElement.click();
  }

  addFiles(event: Event): void {
    const files = Array.from((event.target as HTMLInputElement).files as FileList);

    if (!files.length) {
      return;
    }

    const correctFiles = files.filter((file) => file.size <= this.MAX_FILE_SIZE && this.ALLOWED_FILE_TYPES.includes(file.type) && !this.addedFiles.find((f) => f.name === file.name));
    const incorrectFiles = files.filter((file) => file.size > this.MAX_FILE_SIZE || !this.ALLOWED_FILE_TYPES.includes(file.type) || this.addedFiles.find((f) => f.name === file.name));

    if (correctFiles.length) {
      while (correctFiles.length > 0 && this.addedFiles.length < this.MAX_FILES_COUNT) {
        const file = correctFiles.shift();
        this.fileToBase64(file).then((data) => {
          this.addedFiles.push({
            name: file.name,
            data: data
          });
        });
      }
    }

    incorrectFiles.forEach((file) => {
      if (file.size > this.MAX_FILE_SIZE) {
        this.loggerService.log(LoggerType.Error, `Zbyt duży rozmiar pliku <b>${file.name}</b>.`);
      } else if (!this.ALLOWED_FILE_TYPES.includes(file.type)) {
        this.loggerService.log(LoggerType.Error, `Nieprawidłowy typ pliku <b>${file.name}</b>.`);
      } else {
        this.loggerService.log(LoggerType.Info, `Plik <b>${file.name}</b> został dodany wcześniej.`);
      }
    });

    if (correctFiles.length) {
      this.loggerService.log(LoggerType.Error, `Pliki: <ul><li><b>${correctFiles.map((f) => f.name).join('</b></li><li><b>')}</b></li></ul> nie zostały dodane z powodu przekroczenia limitu plików.`);
    }

    this.fileInput.nativeElement.value = '';
    this.filesAdded.emit(this.addedFiles);
  }

  removeFile(file: ContactFormFile): void {
    this.addedFiles = this.addedFiles.filter((f) => f !== file);
    this.filesAdded.emit(this.addedFiles);
  }

  private fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
}
